import { useCallback } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Form } from "@/components/controls";
import type { IServiceArea } from "@/types/common";
import { Button, Drawer } from "@/ui-kit";
import { ServiceAreasForm } from "./ServiceAreasForm";
import { useServiceAreasForm } from "./useServiceAreasForm";

type IServiceAreasEditModalProps = {
  userId: number;
  serviceAreas: IServiceArea[];
  updateServiceArea: (params: {
    userId: number;
    serviceAreas: IServiceArea[];
  }) => Promise<{ serviceAreas: IServiceArea[] }>;
  onSave: (v: IServiceArea[]) => void;
  onDismiss: () => void;
};

export const ServiceAreasEditModal = ({
  userId,
  serviceAreas,
  updateServiceArea,
  onSave,
  onDismiss,
}: IServiceAreasEditModalProps) => {
  const { t } = useTranslation();

  const handleSuccess = useCallback(
    (response: typeof serviceAreas) => {
      onSave(response);
      onDismiss();
    },
    [onSave, onDismiss],
  );

  const { form, onSubmit, loading } = useServiceAreasForm({
    userId,
    serviceAreas,
    updateServiceArea,
    onSuccess: handleSuccess,
  });

  return (
    <FormProvider {...form}>
      <Drawer
        className="settings__user-modal"
        open={true}
        onOpenChange={onDismiss}
        closeOnOutsidePress={false}
      >
        <Drawer.Header title={t("label.service_areas")} />
        <Drawer.Body>
          <Form>
            <ServiceAreasForm />
          </Form>
        </Drawer.Body>
        <Drawer.Footer>
          <Button onClick={onDismiss} variant="ghost" color="neutral">
            {t("action.cancel")}
          </Button>
          <Button onClick={onSubmit} loading={loading}>
            {t("action.save")}
          </Button>
        </Drawer.Footer>
      </Drawer>
    </FormProvider>
  );
};
